import { useIsAuthenticated } from '@heidi/auth/state';
import { useQuery } from '@tanstack/react-query';

import type { SessionPatientDocumentLinkResponse } from '../../contracts';
import { GET_SESSION_PATIENT_DOCUMENT_LINKS } from '../../contracts';
import { getSessionPatientDocumentLinks } from '../api/patientDocumentLinks';

/**
 * Fetch the patient documents currently linked to a session as note-gen context.
 *
 * Each item already carries the embedded patient-document metadata
 * (file_name, mime_type, size_bytes, description, summary) so the FE can
 * render a row without a second query for the underlying doc.
 *
 * The hook is enabled only when the user is authenticated and a sessionId is
 * present; pass `null`/`undefined` for sessionId to keep the query idle.
 */
export function useQuerySessionPatientDocumentLinks(
  sessionId: string | null | undefined
) {
  const { isAuthenticated } = useIsAuthenticated();

  return useQuery<SessionPatientDocumentLinkResponse[]>({
    queryKey: [GET_SESSION_PATIENT_DOCUMENT_LINKS, { sessionId }],
    enabled: isAuthenticated && !!sessionId,
    queryFn: async () => {
      if (!sessionId) return [];
      const { data } = await getSessionPatientDocumentLinks(sessionId);
      return data.items;
    },
    // Embedded patient-document fields (file_name, description, summary)
    // are PHI; React Query's default `staleTime: 0` would refetch this on
    // every window-focus and reconnect. 60s keeps the data fresh enough
    // for the dialog/badge while cutting unnecessary PHI exposure.
    staleTime: 60_000,
  });
}
